import React, { useEffect, useMemo, useState, useCallback } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { createClientOnEzDB, createErrorLog } from "../api";
import { getUserInfo } from "../api/trello";
import styles from "./PayPalSubscriptionComponent.module.css";
import {
  FaCalendarAlt,
  FaCalendarCheck,
  FaFrown,
  FaQuestionCircle,
  FaTimesCircle,
} from "react-icons/fa";

import Accordion from "react-bootstrap/Accordion";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAndSetAuthStatus,
  fetchAndSetUserInfo,
} from "../redux/authActions";
import { useNavigate } from "react-router-dom";
import { setIsAuthenticated, setPlan } from "../redux/authSlice";
import { Fade } from "react-bootstrap";

/**
 * GEO → Currency map based on the provided table.
 * ipinfo.io/json returns ISO country (e.g., "BR"), so we map ISO → English name → currency.
 */
const COUNTRY_TO_CURRENCY = {
  Netherlands: "EUR", // Países Baixos (Holanda)
  "United States": "USD",
  France: "EUR",
  Germany: "EUR",
  Spain: "EUR",
  Brazil: "BRL",
  Canada: "CAD",
  Italy: "EUR",
  Australia: "AUD",
  Portugal: "EUR",
};

// ISO (ipinfo.country) → English country name we use above
const ISO_TO_NAME = {
  NL: "Netherlands",
  US: "United States",
  FR: "France",
  DE: "Germany",
  ES: "Spain",
  BR: "Brazil",
  CA: "Canada",
  IT: "Italy",
  AU: "Australia",
  PT: "Portugal",
};

/** Plan matrix (currency → price + plan ids) */
const PLAN_MATRIX = {
  EUR: {
    monthly: { value: 1, planId: "P-5W224628MS881642RNDPT2XQ" },
    annual: { value: 10, planId: "P-4E198916W4891825SNDPT54A" },
    locale: "de-DE",
  },
  BRL: {
    monthly: { value: 5, planId: "P-54M02195L3107842NNDPT4KI" },
    annual: { value: 50, planId: "P-82U20069D82823150NDPT4ZI" },
    locale: "pt-BR",
  },
  AUD: {
    monthly: { value: 1.5, planId: "P-4F664832PR3531014NDPT7EQ" },
    annual: { value: 15, planId: "P-6MF43194TB710154SNDPT7XA" },
    locale: "en-AU",
  },
  CAD: {
    monthly: { value: 1.5, planId: "P-7RW35681UT100373YNDPUAYI" },
    annual: { value: 15, planId: "P-65833454SP9203729NDPUBKQ" },
    locale: "en-CA",
  },
  USD: {
    monthly: { value: 1, planId: "P-0FN327861A936262ENCZS7MQ" },
    annual: { value: 10, planId: "P-2AJ286084T463661PNC2INRQ" },
    locale: "en-US",
  },
};

/** Utility: format money with Intl */
function fmt(amount, currency, localeHint) {
  try {
    const locale = localeHint || (PLAN_MATRIX[currency] && PLAN_MATRIX[currency].locale) || undefined;
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch (e) {
    const symbol = currency === "EUR" ? "€" : currency === "BRL" ? "R$" : currency === "AUD" ? "A$" : currency === "CAD" ? "C$" : "$";
    return `${symbol} ${amount}`;
  }
}

const PayPalSubscriptionComponent = () => {
  const { userName, email, isAuthenticated, plan, errorOnAuthRequest } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // GEO / currency state
  const [country, setCountry] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [geoLoading, setGeoLoading] = useState(true);
  const [geoError, setGeoError] = useState(null);

  // Resolve currency based on ipinfo country
  useEffect(() => {
    let isMounted = true;
    const fetchGeo = async () => {
      try {
        setGeoLoading(true);
        setGeoError(null);
        const res = await fetch("https://ipinfo.io/json", { headers: { Accept: "application/json" } });
        if (!res.ok) throw new Error(`ipinfo http ${res.status}`);
        const data = await res.json();
        const iso = data && data.country; // e.g., "BR"
        const resolvedName = (iso && ISO_TO_NAME[iso]) || null;
        const resolvedCurrency = (resolvedName && COUNTRY_TO_CURRENCY[resolvedName]) || "USD";
        if (!isMounted) return;
        setCountry(resolvedName);
        setCurrency(resolvedCurrency);
        setGeoLoading(false);
      } catch (e) {
        if (!isMounted) return;
        setGeoError(e && e.message ? e.message : "geo lookup failed");
        setCountry(null);
        setCurrency("USD");
        setGeoLoading(false);
      }
    };
    fetchGeo();
    return () => {
      isMounted = false;
    };
  }, []);

  // Keep original user info / auth bootstrap
  useEffect(() => {
    if (!userName || !email) {
      dispatch(fetchAndSetUserInfo());
    }
    if (email && !isAuthenticated) {
      dispatch(fetchAndSetAuthStatus(email));
    }
  }, [dispatch, email, userName, isAuthenticated]);

  // Dynamic plan payloads from currency
  const monthlyPlan = useMemo(() => PLAN_MATRIX[currency].monthly, [currency]);
  const annualPlan = useMemo(() => PLAN_MATRIX[currency].annual, [currency]);

  const initialOptions = useMemo(
    () => ({
      clientId:
        "Ac84GaEtL961Mh5CnzVrtzb_qO6S4b78ylFyZjz7oEc_-fmO2L2nvFdVbkt-uw1drOYc92bIzVrTFIUj",
      vault: true,
      intent: "subscription",
    }), []
  );

  const handleGoToImportPage = useCallback((selectedPlan) => {
    const normalized = selectedPlan === "anual" ? "annual" : selectedPlan;
    dispatch(setIsAuthenticated(true));
    dispatch(setPlan(normalized));
    navigate("/imports");
  }, [dispatch, navigate]);

  const createSubscription = useCallback((planType) => (data, actions) => {
    const plan_id = planType === "monthly" ? monthlyPlan.planId : annualPlan.planId;
    return actions.subscription.create({ plan_id });
  }, [monthlyPlan.planId, annualPlan.planId]);

  const onApprove = useCallback(async (data, period) => {
    const userInfo = await getUserInfo();
    const clientData = {
      name: userInfo.name,
      textEmail: userInfo.email,
      assinatura: [
        {
          name: "Sheet Importer",
          metodoPagamentoId: 1,
          textIdentificadorMetodoPagamento: data.subscriptionID,
          periodicidadeId: period === "monthly" ? 1 : 2,
        },
      ],
    };

    try {
      await createClientOnEzDB(clientData);
      handleGoToImportPage(period);
    } catch (error) {
      const err = {
        message: error && error.message,
        tracer: error,
        email,
        url: (error && error.url) || "onApprove-paypal",
      };
      await createErrorLog(err);
    }
  }, [email, handleGoToImportPage]);

  const onCancel = async (_data) => {
    // optional: log cancellations
  };

  const onError = async (error) => {
    const err = {
      message: error && error.message,
      tracer: error,
      email,
      url: (error && error.url) || "onError-paypal",
    };
    await createErrorLog(err);
  };

  const currencyLabel = currency;
  const priceMonthlyStr = fmt(monthlyPlan.value, currencyLabel);
  const priceAnnualStr = fmt(annualPlan.value, currencyLabel);

  return (
    <Fade in appear>
      <div className="w-100 d-flex flex-column justify-content-center align-items-center">
        <div className={`${styles.header} w-100`}>
          <h1>Subscribe to Sheet Importer</h1>
          {!geoLoading && (
            <p className="mb-0" style={{ opacity: 0.85 }}>
              {country ? `Detected location: ${country} · Currency: ${currencyLabel}` : `Using default currency: ${currencyLabel}`}
              {geoError && <span className="text-warning ms-2">(geo lookup fallback)</span>}
            </p>
          )}
        </div>

        <div className={`${styles.description}`}>
          <p className="mb-0">
            <i className="fas fa-info-circle"></i> Once you subscribe to a plan,
            you will be able to use Sheet Importer on any Trello board as the plan will be associated with your user (email).
          </p>
        </div>

        {errorOnAuthRequest && (
          <h2 className={styles.faq_header}>Error retrieving data. Please try again.</h2>
        )}

        {!errorOnAuthRequest && (
          <div className={styles.plan_container}>
            {/* Monthly */}
            {((!isAuthenticated && !plan) || plan === "monthly") && (
              <div className={styles.plan}>
                <h2>
                  <FaCalendarAlt /> <br /> Monthly Plan
                </h2>
                <p>{priceMonthlyStr} per month</p>
                <div className={styles.paypal_button_container} id="paypal-button-container-monthly">
                  {(!plan || plan === "annual" || plan === "anual") && (
                    <PayPalScriptProvider options={initialOptions}>
                      <PayPalButtons
                        style={{ shape: "rect", color: "blue", layout: "vertical", label: "subscribe" }}
                        createSubscription={createSubscription("monthly")}
                        onApprove={(data) => onApprove(data, "monthly")}
                        onCancel={onCancel}
                        onError={onError}
                      />
                    </PayPalScriptProvider>
                  )}
                  {plan === "monthly" && (
                    <span className={styles.span_sub}>Congratulations, you already have a subscription to Sheet Importer.</span>
                  )}
                </div>
              </div>
            )}

            {/* Annual */}
            {((!isAuthenticated && !plan) || plan === "annual" || plan === "anual") && (
              <div className={styles.plan}>
                <h2>
                  <FaCalendarCheck /> <br /> Annual Plan
                </h2>
                <p>{priceAnnualStr} per year</p>
                <div className={styles.paypal_button_container} id="paypal-button-container-annual">
                  {(!plan || plan === "monthly") && (
                    <PayPalScriptProvider options={initialOptions}>
                      <PayPalButtons
                        style={{ shape: "rect", color: "gold", layout: "vertical", label: "subscribe" }}
                        createSubscription={createSubscription("annual")}
                        onApprove={(data) => onApprove(data, "annual")}
                        onCancel={onCancel}
                        onError={onError}
                      />
                    </PayPalScriptProvider>
                  )}
                  {(plan === "annual" || plan === "anual") && (
                    <span className={styles.span_sub}>Congratulations, you already have a subscription to Sheet Importer.</span>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* FAQ */}
        <div className={styles.faq_section}>
          <h2 className={styles.faq_header}>
            <FaQuestionCircle className="mx-2" style={{ color: "#007bff" }} /> Frequently Asked Questions
          </h2>
          <Accordion className="w-100 px-3">
            <Accordion.Item eventKey="0">
              <Accordion.Header>
                <h4 className={styles.faq_item}>
                  <FaFrown className="mx-2" style={{ color: "#ffc107" }} /> What is the trial period and how does it work?
                </h4>
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  You will have 3 days to test and make unlimited imports. If you find Sheet Importer meets your needs,
                  do nothing, and your subscription will automatically begin after the trial period, charging you for the plan you selected.
                  To avoid charges, please cancel the subscription through PayPal before the 7th day.
                </p>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="1">
              <Accordion.Header>
                <h4 className={styles.faq_item}>
                  <FaFrown className="mx-2" style={{ color: "#dc3545" }} /> I'm not happy, can I have my money back?
                </h4>
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  We have a 30-day no questions asked money-back guarantee. Simply drop us an email at
                  {" "}
                  <a href="mailto:support.Sheet Importer (to xlsx and csv)@smartezy.com.br" style={{ textDecoration: "none", color: "#007bff" }}>
                    support.Sheet Importer (to xlsx and csv)@smartezy.com.br
                  </a>
                  , and we'll process your refund swiftly.
                </p>
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>
                <h4 className={styles.faq_item}>
                  <FaTimesCircle className="mx-2" style={{ color: "#17a2b8" }} /> How can I cancel my subscription?
                </h4>
              </Accordion.Header>
              <Accordion.Body>
                <p>
                  To cancel your subscription, please follow these instructions from PayPal: <br />
                  <br />
                  <strong>1</strong>. Log in to your PayPal account.
                  <br />
                  <strong>2</strong>. Click on "Settings" (gear icon) next to "Log out".
                  <br />
                  <strong>3</strong>. Select "Payments".
                  <br />
                  <strong>4</strong>. Click "Manage automatic payments".
                  <br />
                  <strong>5</strong>. Select the subscription you want to cancel and click "Cancel".
                  <br />
                  <br />
                  For more details, visit{" "}
                  <a
                    href="https://www.paypal.com/us/smarthelp/article/FAQ1067"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "none", color: "#28a745" }}
                  >
                    PayPal Help
                  </a>
                  .
                </p>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>

        <div className={styles.footer}>
          <p>
            Sheet Importer is a product of SmartEzy. For assistance or inquiries, please email us at {" "}
            <a href="mailto:support.Sheet Importer (to xlsx and csv)@smartezy.com.br">support.Sheet Importer (to xlsx and csv)@smartezy.com.br</a>.
          </p>
        </div>
      </div>
    </Fade>
  );
};

export default PayPalSubscriptionComponent;
